// EventsCalendarPanel.jsx — Admin-only events + weather + self-learning panel.
//
// Three tabs:
//   📅 الفعاليات        — list, add, edit, delete events + seed Saudi calendar
//   🌤 الطقس           — 7-day forecast + recent observations
//   🧠 ما تعلّمه النظام — learned multipliers per event/weather × product
//
// Endpoints: /api/products/events/* and /api/products/weather/*
import React, { useEffect, useState, useCallback } from "react";
import { api } from "./lib/api";
import { showToast } from "./lib/theme";
import DateField from "./components/DateField";

const TYPES = [
  { id: "religious", label: "🕌 ديني / موسمي", color: "#7C3AED" },
  { id: "school",    label: "🎒 دراسي",        color: "#0891B2" },
  { id: "local",     label: "📍 محلي / حدث",   color: "#16A34A" },
  { id: "seasonal",  label: "🌤 موسمي",         color: "#D97706" },
];

const TABS = [
  { id: "events",  label: "📅 الفعاليات" },
  { id: "weather", label: "🌤 الطقس" },
  { id: "learned", label: "🧠 تعلّم النظام" },
];

const fmt = (n) => Math.round(n || 0).toLocaleString("en-US");

export default function EventsCalendarPanel() {
  const [tab, setTab] = useState("events");
  return (
    <div data-testid="events-calendar-panel">
      <div style={{ background: "linear-gradient(135deg, #fff 0%, #FEF3C7 100%)",
        padding: 16, marginBottom: 14, borderRadius: 8,
        borderInlineStart: "4px solid #D97706" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#92400E", marginBottom: 4 }}>
          📅 الفعاليات والمناسبات + الطقس
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
          النظام يستخدم هذه السياقات لتعديل توقع المبيعات تلقائياً — وعند انتهاء كل فعالية يتعلّم
          مضاعفاً جديداً لكل منتج بناءً على المبيعات الفعلية.
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`evc-tab-${t.id}`}
            style={{ padding: "8px 16px", borderRadius: 6, fontSize: 12.5, fontWeight: 700,
              cursor: "pointer", border: "1px solid #D97706",
              background: tab === t.id ? "#D97706" : "#fff",
              color: tab === t.id ? "#fff" : "#92400E" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "events"  && <EventsTab />}
      {tab === "weather" && <WeatherTab />}
      {tab === "learned" && <LearnedTab />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Events Tab
// ════════════════════════════════════════════════════════════════════
function EventsTab() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/products/events/list");
      setData(data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedSaudi = async () => {
    if (!window.confirm("سيتم إضافة 13 فعالية من التقويم السعودي. متابعة؟")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/products/events/seed-saudi");
      showToast(`✅ تم إضافة ${data.inserted} فعالية`, "success");
      load();
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل البذر", "error");
    } finally { setBusy(false); }
  };

  const learn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/products/events/learn");
      showToast(`🧠 معالجة ${data.events_processed} فعالية · تعلّم الطقس: ${data.weather?.learned || 0}`, "success");
      load();
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل التعلّم", "error");
    } finally { setBusy(false); }
  };

  const delEvent = async (id) => {
    if (!window.confirm("حذف هذه الفعالية وجميع المضاعفات المتعلّمة منها؟")) return;
    try {
      await api.delete(`/products/events/${id}`);
      showToast("🗑 تم الحذف", "success");
      load();
    } catch (e) { showToast(e?.response?.data?.detail || "فشل الحذف", "error"); }
  };

  if (!data) return <div style={{ padding: 20, textAlign: "center" }}>⏳ جارٍ التحميل...</div>;

  const items = data.items || [];
  const today = data.today;
  const upcoming = items.filter((e) => e.end_date >= today);
  const past = items.filter((e) => e.end_date < today);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="evc-add-btn"
          style={{ padding: "8px 14px", background: "#D97706", color: "#fff", border: "none",
            borderRadius: 6, fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
          ➕ إضافة فعالية
        </button>
        <button onClick={seedSaudi} disabled={busy} data-testid="evc-seed-btn"
          style={{ padding: "8px 14px", background: "#fff", color: "#92400E",
            border: "1px solid #D97706", borderRadius: 6, fontWeight: 700, fontSize: 12.5,
            cursor: busy ? "wait" : "pointer" }}>
          📥 بذر التقويم السعودي
        </button>
        <button onClick={learn} disabled={busy} data-testid="evc-learn-btn"
          style={{ padding: "8px 14px", background: "#7C3AED", color: "#fff", border: "none",
            borderRadius: 6, fontWeight: 700, fontSize: 12.5, cursor: busy ? "wait" : "pointer" }}>
          🧠 شغّل التعلّم الآن
        </button>
      </div>

      {showForm && (
        <EventForm event={editing} onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}

      <EventsList title="📅 القادمة والجارية" events={upcoming} today={today}
        onEdit={(e) => { setEditing(e); setShowForm(true); }} onDelete={delEvent}
        testidPrefix="evc-up" />

      <EventsList title="✅ المنتهية" events={past} today={today}
        onEdit={(e) => { setEditing(e); setShowForm(true); }} onDelete={delEvent}
        testidPrefix="evc-past" />
    </div>
  );
}

function EventsList({ title, events, today, onEdit, onDelete, testidPrefix }) {
  if (!events.length) return null;
  return (
    <div className="card" style={{ padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>
        {title} ({events.length})
      </div>
      <div className="tw">
        <table>
          <thead>
            <tr>
              <th>الاسم</th><th>النوع</th><th>البداية</th><th>النهاية</th>
              <th>التأثير</th><th>تعلّم</th><th>المصدر</th><th></th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => {
              const t = TYPES.find((x) => x.id === e.type) || TYPES[2];
              const isActive = e.start_date <= today && e.end_date >= today;
              return (
                <tr key={e.id || i} data-testid={`${testidPrefix}-${i}`}
                  style={{ background: isActive ? "#FEF3C7" : undefined }}>
                  <td style={{ fontWeight: 700 }}>
                    {e.name}
                    {isActive && <span style={{ marginInlineStart: 6, fontSize: 10, color: "#D97706" }}>● جارية</span>}
                  </td>
                  <td>
                    <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10.5,
                      background: t.color + "22", color: t.color, fontWeight: 700 }}>
                      {t.label}
                    </span>
                  </td>
                  <td>{e.start_date}</td>
                  <td>{e.end_date}</td>
                  <td style={{ fontWeight: 700,
                    color: e.default_multiplier > 1 ? "#16A34A" : e.default_multiplier < 1 ? "#DC2626" : "var(--text)" }}>
                    ×{e.default_multiplier}
                  </td>
                  <td>{e.learned_products || 0} منتج</td>
                  <td style={{ fontSize: 11, color: "var(--muted)" }}>
                    {e.source === "saudi_seed" ? "🇸🇦 افتراضي" : "👤 مخصص"}
                  </td>
                  <td>
                    <button onClick={() => onEdit(e)}
                      style={{ padding: "3px 8px", background: "#0891B2", color: "#fff",
                        border: "none", borderRadius: 4, fontSize: 10.5, cursor: "pointer", marginInlineEnd: 4 }}>
                      ✎
                    </button>
                    <button onClick={() => onDelete(e.id)}
                      style={{ padding: "3px 8px", background: "#DC2626", color: "#fff",
                        border: "none", borderRadius: 4, fontSize: 10.5, cursor: "pointer" }}>
                      🗑
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EventForm({ event, onClose, onSaved }) {
  const isEdit = !!event?.id;
  const [name, setName] = useState(event?.name || "");
  const [start, setStart] = useState(event?.start_date || "");
  const [end, setEnd] = useState(event?.end_date || "");
  const [type, setType] = useState(event?.type || "local");
  const [desc, setDesc] = useState(event?.description || "");
  const [mult, setMult] = useState(event?.default_multiplier || 1.15);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const errStyle = (k) => errors[k] ? { borderColor: "#DC2626", background: "#FEE2E2" } : {};
  const errMsg = (k) => errors[k]
    ? <div style={{ fontSize: 10.5, color: "#DC2626", marginTop: 2 }}>{errors[k]}</div>
    : null;

  const save = async () => {
    const errs = {};
    if (!name.trim()) errs.name = "اسم الفعالية مطلوب";
    if (!start) errs.start = "تاريخ البداية مطلوب";
    if (!end) errs.end = "تاريخ النهاية مطلوب";
    if (start && end && end < start) errs.end = "النهاية قبل البداية";
    setErrors(errs);
    if (Object.keys(errs).length) {
      showToast("الرجاء تصحيح الحقول الملوّنة بالأحمر", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), start_date: start, end_date: end,
        type, description: desc, default_multiplier: parseFloat(mult) };
      if (isEdit) await api.patch(`/products/events/${event.id}`, payload);
      else await api.post("/products/events/create", payload);
      showToast("✅ تم الحفظ", "success");
      onSaved();
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل الحفظ", "error");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ padding: 14, marginBottom: 12, background: "#FFFBEB",
      borderInlineStart: "4px solid #D97706" }} data-testid="evc-form">
      <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>
        {isEdit ? "✎ تعديل فعالية" : "➕ فعالية جديدة"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
        <Field label="🎯 الاسم">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="evc-form-name" placeholder="مثال: حفل قريب من الفرع"
            style={{ width: "100%", padding: 8, border: "1px solid var(--border)", borderRadius: 4, ...errStyle("name") }} />
          {errMsg("name")}
        </Field>
        <Field label="🏷 النوع">
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="evc-form-type"
            style={{ width: "100%", padding: 8, border: "1px solid var(--border)", borderRadius: 4 }}>
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="📅 البداية">
          <DateField  value={start} onChange={(e) => setStart(e.target.value)}
            data-testid="evc-form-start"
            style={{ width: "100%", padding: 8, border: "1px solid var(--border)", borderRadius: 4, ...errStyle("start") }} />
          {errMsg("start")}
        </Field>
        <Field label="📅 النهاية">
          <DateField  value={end} onChange={(e) => setEnd(e.target.value)}
            data-testid="evc-form-end"
            style={{ width: "100%", padding: 8, border: "1px solid var(--border)", borderRadius: 4, ...errStyle("end") }} />
          {errMsg("end")}
        </Field>
        <Field label={`📊 المضاعف الافتراضي (×${mult}) — أكبر من 1 = ارتفاع، أقل = انخفاض`}>
          <input type="range" min="0.5" max="2.5" step="0.05" value={mult}
            onChange={(e) => setMult(e.target.value)} data-testid="evc-form-mult"
            style={{ width: "100%" }} />
        </Field>
        <Field label="📝 وصف (اختياري)">
          <input type="text" value={desc} onChange={(e) => setDesc(e.target.value)}
            data-testid="evc-form-desc"
            style={{ width: "100%", padding: 8, border: "1px solid var(--border)", borderRadius: 4 }} />
        </Field>
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button onClick={save} disabled={saving} data-testid="evc-form-save"
          style={{ padding: "8px 16px", background: "#D97706", color: "#fff",
            border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}>
          {saving ? "⏳ جارٍ الحفظ..." : isEdit ? "حفظ التعديل" : "إضافة"}
        </button>
        <button onClick={onClose}
          style={{ padding: "8px 16px", background: "#fff", color: "#92400E",
            border: "1px solid #D97706", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}>
          إلغاء
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4, fontWeight: 600 }}>{label}</div>
      {children}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Weather Tab
// ════════════════════════════════════════════════════════════════════
function WeatherTab() {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products/weather/forecast?days=7");
        setForecast(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div style={{ padding: 20, textAlign: "center" }}>⏳ جارٍ تحميل الطقس...</div>;
  if (!forecast?.days?.length) {
    return <div style={{ padding: 16, color: "var(--muted)", textAlign: "center" }}>
      ⚠ تعذّر الاتصال بـ Open-Meteo. سيُعاد المحاولة لاحقاً.
    </div>;
  }

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = forecast.days.filter((d) => d.date >= today).slice(0, 7);

  return (
    <div data-testid="evc-weather-tab">
      <div className="card" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>
          🌤 توقع الطقس — الأسبوع القادم (مصدر: Open-Meteo)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
          {upcoming.map((d, i) => {
            const c = d.bucket === "hot" ? "#DC2626"
                  : d.bucket === "cold" ? "#0891B2" : "#D97706";
            const icon = d.bucket === "hot" ? "🔥" : d.bucket === "cold" ? "❄" : "🌤";
            return (
              <div key={i} data-testid={`evc-weather-${i}`} style={{
                padding: 10, background: "#fff", borderRadius: 6,
                border: `1px solid ${c}33`, borderInlineStart: `4px solid ${c}`,
              }}>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>{d.date}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: c, marginTop: 4 }}>
                  {icon} {d.temp_max_c}°
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
                  دنيا {d.temp_min_c}° · {d.precip_mm ? `${d.precip_mm}مم مطر` : "بدون مطر"}
                </div>
                <div style={{ fontSize: 10, marginTop: 4, color: c, fontWeight: 700 }}>
                  {d.bucket === "hot" ? "حار — يدفع المبيعات الباردة"
                    : d.bucket === "cold" ? "بارد — يدفع المبيعات الحارة"
                    : "معتدل"}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: "var(--muted)", textAlign: "center" }}>
          📍 الرياض (24.71, 46.68) · مصدر مجاني بدون مفتاح API
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Learned Tab
// ════════════════════════════════════════════════════════════════════
function LearnedTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products/events/learned");
        setData(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!data) return <div style={{ padding: 20, textAlign: "center" }}>⏳ جارٍ التحميل...</div>;
  const evs = data.events || [];
  const ws = data.weather || [];

  // Friendly empty state when both tables are empty
  if (evs.length === 0 && ws.length === 0) {
    return (
      <div data-testid="evc-learned-tab" className="card" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🧠</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#7C3AED", marginBottom: 8 }}>
          لم يتم تعلّم أي مضاعفات بعد
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.8, maxWidth: 520, margin: "0 auto" }}>
          النظام يتعلّم تلقائياً بعد انتهاء كل فعالية مع توفّر تقارير منتجات يومية داخل فترتها.<br />
          <b>الخطوات:</b> اذهب لتبويب «📅 الفعاليات» → اضغط «📥 بذر التقويم السعودي» (إن لم يُفعّل بعد) → ثم
          «🧠 شغّل التعلّم الآن». سيظهر المضاعف هنا بعد التحليل.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="evc-learned-tab">
      <div className="card" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED", marginBottom: 10 }}>
          🧠 ما تعلّمه النظام من الفعاليات ({evs.length})
        </div>
        {evs.length === 0 ? (
          <div style={{ padding: 16, color: "var(--muted)", textAlign: "center" }}>
            لا توجد بيانات تعلّم بعد. اضغط «شغّل التعلّم الآن» بعد انتهاء أي فعالية مع تقارير متعددة.
          </div>
        ) : (
          <div className="tw">
            <table data-testid="evc-learned-events">
              <thead><tr><th>الفعالية</th><th>المنتج</th><th>المضاعف المتعلّم</th><th>أيام رُصدت</th><th>الثقة</th></tr></thead>
              <tbody>
                {evs.map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{r.event_name}</td>
                    <td>{r.product_name}</td>
                    <td style={{ fontWeight: 700,
                      color: r.learned_multiplier > 1 ? "#16A34A" : "#DC2626" }}>
                      ×{r.learned_multiplier}
                    </td>
                    <td>{r.days_observed}</td>
                    <td>
                      <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11,
                        background: "#F5F3FF", color: "#7C3AED", fontWeight: 700 }}>
                        {r.confidence}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0891B2", marginBottom: 10 }}>
          🌡 ما تعلّمه النظام من الطقس ({ws.length})
        </div>
        {ws.length === 0 ? (
          <div style={{ padding: 16, color: "var(--muted)", textAlign: "center" }}>
            لا توجد بيانات طقس متعلّمة بعد. النظام يحتاج لـ ≥5 ملاحظات طقس مع بيانات مبيعات يومية متوافقة.
          </div>
        ) : (
          <div className="tw">
            <table data-testid="evc-learned-weather">
              <thead><tr><th>الفئة</th><th>المنتج</th><th>المضاعف</th><th>الأيام</th><th>الثقة</th></tr></thead>
              <tbody>
                {ws.map((r, i) => (
                  <tr key={i}>
                    <td>{r.bucket === "hot" ? "🔥 حار (≥35°)"
                       : r.bucket === "cold" ? "❄ بارد (<20°)" : "🌤 معتدل"}</td>
                    <td>{r.product_name}</td>
                    <td style={{ fontWeight: 700,
                      color: r.learned_multiplier > 1 ? "#16A34A" : "#DC2626" }}>
                      ×{r.learned_multiplier}
                    </td>
                    <td>{r.days_observed}</td>
                    <td>
                      <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11,
                        background: "#ECFEFF", color: "#0891B2", fontWeight: 700 }}>
                        {r.confidence}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
